import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import RolesList from './RolesList';
import RoleModules from './RoleModules';
import ModuleDetails from './ModuleDetails';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const ROLE_INFO = {
  admin: { id: 'admin', name: 'Administrateur', description: 'Le rôle administrateur a un accès complet à toutes les fonctionnalités et paramètres de la pharmacie.', color: 'red', type: 'Système', code: 'ADMIN', responsibilities: ['Gestion complète des données', 'Gestion des utilisateurs et rôles', 'Configuration des paramètres', 'Accès à tous les rapports et analyses'] },
  gerant: { id: 'gerant', name: 'Gestionnaire', description: 'Gère les opérations et les données', color: 'purple', type: 'Système', code: 'GERANT', responsibilities: ['Gestion des stocks', 'Validation des commandes', 'Accès aux rapports basiques'] },
  caissier: { id: 'caissier', name: 'Caissier', description: 'Accès limité à la caisse et aux ventes', color: 'green', type: 'Système', code: 'CAISSIER', responsibilities: ['Encaissement des ventes', 'Clôture de caisse journalière', 'Création de clients'] },
  assistant: { id: 'assistant', name: 'Assistant', description: 'Accès restreint à certaines fonctionnalités', color: 'yellow', type: 'Système', code: 'ASSISTANT', responsibilities: ['Consultation des stocks', 'Saisie de ventes en attente'] },
  comptable: { id: 'comptable', name: 'Comptable', description: 'Accès aux rapports et à la comptabilité', color: 'blue', type: 'Système', code: 'COMPTABLE', responsibilities: ['Exportation comptable', 'Analyse du PnL', 'Gestion des factures fournisseurs'] },
};


// Helper pour aplatir les permissions (ex: caisse: { entrees: {} } -> 'caisse.entrees': {})
const flattenPermissions = (perms) => {
  const flat = {};
  for (const [key, val] of Object.entries(perms)) {
    if (val && typeof val === 'object' && !Array.isArray(val) && Object.keys(val).some(k => typeof val[k] === 'object')) {
      for (const [subKey, subVal] of Object.entries(val)) {
        flat[`${key}.${subKey}`] = subVal;
      }
    } else {
      flat[key] = val;
    }
  }
  return flat;
};

// Helper pour reconstruire l'objet imbriqué avant la sauvegarde
const unflattenPermissions = (flatPerms) => {
  const nested = {};
  for (const [key, val] of Object.entries(flatPerms)) {
    if (key.includes('.')) {
      const [parent, child] = key.split('.');
      if (!nested[parent]) nested[parent] = {};
      nested[parent][child] = val;
    } else {
      nested[key] = val;
    }
  }
  return nested;
};

export const PERMISSIONS_DEFS = {
  revenues: {
    id: 'revenues', name: 'Revenus', description: 'Gérer les revenus, ventes et retours',
    gestion: [
      { id: 'create', label: 'Ajouter un revenu', description: '' },
      { id: 'update', label: 'Modifier un revenu', description: '' },
      { id: 'delete', label: 'Supprimer un revenu', description: '' }
    ],
    consultation: [
      { id: 'view_history', label: "Voir l'historique des revenus", description: '' },
      { id: 'view_totals', label: 'Voir les totaux et KPIs', description: '' },
      { id: 'filter_period', label: 'Filtrer par période', description: '' },
      { id: 'view_all_users', label: 'Voir les entrées de tous les utilisateurs', description: '' },
      { id: 'export', label: 'Exporter les revenus', description: '' }
    ]
  },

  'caisse.entrees': {
    id: 'caisse.entrees', name: 'Caisse - Entrées', description: 'Gérer les entrées de caisse',
    gestion: [
      { id: 'create', label: 'Effectuer une entrée/clôture' },
      { id: 'update', label: 'Modifier une entrée' },
      { id: 'delete', label: 'Supprimer une entrée' },
      { id: 'validate', label: 'Valider les entrées' }
    ],
    consultation: [
      { id: 'view_history', label: "Voir l'historique" },
      { id: 'view_totals', label: 'Voir les totaux' },
      { id: 'view_all_users', label: 'Voir tous les utilisateurs' },
      { id: 'filter_period', label: 'Filtrer par période' }
    ]
  },
  'caisse.sorties': {
    id: 'caisse.sorties', name: 'Caisse - Sorties', description: 'Gérer les sorties et dépenses',
    gestion: [
      { id: 'create', label: 'Ajouter une sortie' },
      { id: 'update', label: 'Modifier une sortie' },
      { id: 'delete', label: 'Supprimer une sortie' }
    ],
    consultation: [
      { id: 'view_history', label: "Voir l'historique" },
      { id: 'view_totals', label: 'Voir les totaux' },
      { id: 'view_all_users', label: 'Voir tous les utilisateurs' },
      { id: 'filter_period', label: 'Filtrer par période' }
    ]
  },
  'caisse.tresorerie': {
    id: 'caisse.tresorerie', name: 'Trésorerie', description: 'Gérer la trésorerie',
    gestion: [
      { id: 'manage_accounts', label: 'Gérer les comptes' }
    ],
    consultation: [
      { id: 'view', label: 'Consulter la trésorerie' },
      { id: 'view_totals', label: 'Voir les totaux' },
      { id: 'view_transfers', label: 'Voir les transferts' },
      { id: 'filter_period', label: 'Filtrer par période' }
    ]
  },
  factures_bl: {
    id: 'factures_bl', name: 'Factures & BL', description: 'Gérer les factures et bons de livraison',
    gestion: [
      { id: 'create', label: 'Créer une facture/BL' },
      { id: 'update', label: 'Modifier une facture/BL' },
      { id: 'delete', label: 'Supprimer une facture/BL' },
      { id: 'validate', label: 'Valider une facture/BL' }
    ],
    consultation: [
      { id: 'view', label: 'Accéder à la page' },
      { id: 'view_history', label: 'Voir l\'historique' },
      { id: 'view_totals', label: 'Voir les KPIs et totaux' },
      { id: 'filter_period', label: 'Filtrer par période' }
    ]
  },

  pnl: {
    id: 'pnl', name: 'PnL', description: 'Accéder au Profit & Loss',
    gestion: [],
    consultation: [
      { id: 'view', label: 'Accéder à la page PnL' },
      { id: 'view_totals', label: 'Voir les totaux et marges' },
      { id: 'filter_period', label: 'Filtrer par période' }
    ]
  },
  securite_sociale: {
    id: 'securite_sociale', name: 'Sécurité Sociale', description: 'Gérer les paiements sécurité sociale',
    gestion: [
      { id: 'create', label: 'Ajouter un paiement' },
      { id: 'update', label: 'Modifier un paiement' },
      { id: 'delete', label: 'Supprimer un paiement' }
    ],
    consultation: [
      { id: 'view_history', label: 'Voir l\'historique' },
      { id: 'view_totals', label: 'Voir les totaux' },
      { id: 'filter_period', label: 'Filtrer par période' }
    ]
  },
  suppliers: {
    id: 'suppliers', name: 'Fournisseurs', description: 'Gérer les fournisseurs et achats',
    gestion: [
      { id: 'create', label: 'Ajouter' },
      { id: 'update', label: 'Modifier' },
      { id: 'delete', label: 'Supprimer' },
      { id: 'validate', label: 'Valider' }
    ],
    consultation: [
      { id: 'view', label: 'Voir la liste' }
    ]
  },
  analytics: {
    id: 'analytics', name: 'Analyses', description: 'Accéder au module Analytics',
    gestion: [],
    consultation: [
      { id: 'view', label: 'Accéder au module Analytics' }
    ]
  },
  parametres: {
    id: 'parametres', name: 'Paramètres', description: 'Gérer les paramètres de la pharmacie',
    gestion: [
      { id: 'manage', label: 'Gérer les paramètres généraux' }
    ],
    consultation: [
      { id: 'view', label: 'Consulter les paramètres' }
    ]
  },
  banque: {
    id: 'banque', name: 'Banque', description: 'Gérer la banque et les transactions',
    gestion: [
      { id: 'create', label: 'Ajouter une transaction bancaire' },
      { id: 'update', label: 'Modifier une transaction' },
      { id: 'delete', label: 'Supprimer une transaction' }
    ],
    consultation: [
      { id: 'view', label: 'Consulter la banque' }
    ]
  },
  closures: {
    id: 'closures', name: 'Clôtures', description: 'Gérer les clôtures de caisse (journalières/périodiques)',
    gestion: [
      { id: 'create', label: 'Effectuer une clôture' },
      { id: 'update', label: 'Modifier une clôture' },
      { id: 'delete', label: 'Supprimer une clôture' },
      { id: 'validate', label: 'Valider une clôture' }
    ],
    consultation: [
      { id: 'view_history', label: 'Consulter l\'historique des clôtures' },
      { id: 'view_totals', label: 'Voir les totaux' },
      { id: 'view_all_users', label: 'Voir toutes les clôtures (tous utilisateurs)' },
      { id: 'filter_period', label: 'Filtrer par période' }
    ]
  },
  users: {
    id: 'users', name: 'Utilisateurs', description: 'Gérer les utilisateurs et les rôles',
    gestion: [
      { id: 'create', label: 'Ajouter un utilisateur' },
      { id: 'update', label: 'Modifier un utilisateur' },
      { id: 'delete', label: 'Supprimer un utilisateur' },
      { id: 'manage_permissions', label: 'Gérer les permissions' }
    ],
    consultation: [
      { id: 'view', label: 'Voir la liste des utilisateurs' }
    ]
  }
};

function RolesAndPermissionsView({ pharmacy, currentUser, rolesSummary, users, onRefresh }) {
  const [selectedRole, setSelectedRole] = useState(ROLE_INFO.admin);
  const [selectedModule, setSelectedModule] = useState(null);
  
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (selectedRole && pharmacy) {
      fetchRolePermissions();
      // Default to selecting the first module if none selected
      if (!selectedModule) setSelectedModule(PERMISSIONS_DEFS.revenues);
    }
  }, [selectedRole, pharmacy]);

  const fetchRolePermissions = async () => {
    setLoading(true);
    try {
      const headers = { 
        'X-User-Id': currentUser.id,
        'X-Session-Token': currentUser.session_token || '',
        'X-Pharmacy-Id': pharmacy.id
      };
      const res = await axios.get(`${API}/users?role=${selectedRole.id}`, { headers });
      const roleUsers = res.data.filter(u => u.pharmacies?.some(p => p.pharmacy_id === pharmacy.id && p.role === selectedRole.id));
      
      if (roleUsers.length > 0 && roleUsers[0].granular_permissions) {
        setPermissions(flattenPermissions(roleUsers[0].granular_permissions));
      } else {
        setPermissions({});
      }
    } catch (error) {
      console.error('Error fetching role permissions', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (moduleId, permId, allowed) => {
    setPermissions(prev => {
      const newPerms = { ...prev };
      if (!newPerms[moduleId]) newPerms[moduleId] = {};
      
      if (allowed === null) {
        delete newPerms[moduleId][permId];
      } else {
        newPerms[moduleId][permId] = allowed;
      }
      return newPerms;
    });
  };

  const setModuleFullAccess = (moduleId) => {
    setPermissions(prev => {
      const newPerms = { ...prev };
      if (!newPerms[moduleId]) newPerms[moduleId] = {};
      
      const mod = PERMISSIONS_DEFS[moduleId];
      [...mod.gestion, ...mod.consultation].forEach(p => {
        newPerms[moduleId][p.id] = true;
      });
      return newPerms;
    });
  };

  const handleSavePermissions = async () => {
    setSaving(true);
    try {
      const headers = { 
        'X-User-Id': currentUser.id,
        'X-Session-Token': currentUser.session_token || '',
        'X-Pharmacy-Id': pharmacy.id
      };
      await axios.put(
        `${API}/pharmacies/${pharmacy.id}/roles/${selectedRole.id}/permissions`,
        { granular_permissions: unflattenPermissions(permissions) },
        { headers }
      );
      toast.success('Permissions du rôle mises à jour avec succès');
      onRefresh();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde des permissions');
    } finally {
      setSaving(false);
    }
  };

  const getRoleCount = (roleId) => {
    const summary = rolesSummary.find(r => r.role === roleId);
    return summary ? summary.count : 0;
  };

  return (
    <div className="bg-slate-50/50 overflow-x-auto">
      <div className="flex w-full p-3 sm:p-5 gap-3 sm:gap-4" style={{ minHeight: '560px', alignItems: 'stretch' }}>
        
        {/* Colonne 1 : Liste des rôles */}
        <div className="w-[220px] sm:w-[260px] shrink-0 flex flex-col">
          <RolesList 
            rolesInfo={ROLE_INFO}
            selectedRole={selectedRole}
            setSelectedRole={setSelectedRole}
            getRoleCount={getRoleCount}
          />
        </div>

        {/* Colonne 2 : Modules du rôle */}
        <div className="w-[340px] sm:w-[420px] shrink-0 flex flex-col bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <RoleModules 
            role={selectedRole}
            permissions={permissions}
            modules={PERMISSIONS_DEFS}
            selectedModule={selectedModule}
            setSelectedModule={setSelectedModule}
            loading={loading}
          />
        </div>

        {/* Colonne 3 : Détails du module */}
        <div className="flex-1 min-w-[260px] flex flex-col bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {selectedModule ? (
            <ModuleDetails 
              module={selectedModule}
              permissions={permissions}
              onPermissionChange={handlePermissionChange}
              onSetFullAccess={() => setModuleFullAccess(selectedModule.id)}
              onSave={handleSavePermissions}
              saving={saving}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400 p-8 text-center text-sm">
              Sélectionnez un module pour voir ses droits d'accès
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

export default RolesAndPermissionsView;
